import React, { useState } from 'react';
import { Table2, BarChart2, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 50;

export default function ResultsTable({ rows, columns, error, onVisualizeClick }) {
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(0);

  if (error) {
    return (
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: 10, padding: 24,
      }}>
        <AlertTriangle size={18} style={{ color: 'var(--red)' }} />
        <div style={{
          color: 'var(--red)', fontFamily: 'var(--mono)', fontSize: 12,
          background: 'rgba(224,82,82,0.07)', padding: '10px 18px',
          borderRadius: 4, border: '1px solid rgba(224,82,82,0.2)',
          maxWidth: 600, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          {error}
        </div>
      </div>
    );
  }

  if (!rows) {
    return (
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: 8, color: 'var(--text3)',
      }}>
        <Table2 size={24} style={{ opacity: 0.3 }} />
        <span style={{ fontSize: 12 }}>Run a query to see results</span>
      </div>
    );
  }

  const sorted = [...rows].sort((a, b) => {
    if (!sortCol) return 0;
    const av = a[sortCol], bv = b[sortCol];
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
    return sortDir === 'asc'
      ? String(av).localeCompare(String(bv))
      : String(bv).localeCompare(String(av));
  });

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const pageRows = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
    setPage(0);
  };

  const numericCols = columns.filter(c => rows.length > 0 && typeof rows[0][c] === 'number');

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
        padding: '5px 12px', background: 'var(--bg2)', borderBottom: '1px solid var(--border)',
      }}>
        <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 600 }}>results</span>
        <span style={{ color: 'var(--border2)' }}>·</span>
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>
          {rows.length.toLocaleString()} rows · {columns.length} cols
        </span>
        {numericCols.length > 0 && (
          <>
            <div style={{ flex: 1 }} />
            <button
              onClick={onVisualizeClick}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '4px 10px',
                background: 'var(--bg3)', border: '1px solid var(--border2)',
                borderRadius: 4, color: 'var(--text2)',
                fontFamily: 'var(--sans)', fontSize: 11,
                cursor: 'pointer', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text2)'; }}
            >
              <BarChart2 size={11} />
              Visualize
            </button>
          </>
        )}
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <table style={{
          width: '100%', borderCollapse: 'collapse',
          fontFamily: 'var(--sans)', fontSize: 13,
        }}>
          <thead>
            <tr style={{ background: 'var(--bg3)', position: 'sticky', top: 0, zIndex: 1 }}>
              {columns.map(col => (
                <th
                  key={col}
                  onClick={() => handleSort(col)}
                  style={{
                    padding: '8px 16px', borderBottom: '1px solid var(--border)',
                    color: sortCol === col ? 'var(--text)' : 'var(--text2)',
                    fontWeight: 400, fontSize: 13, textAlign: 'left',
                    cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none',
                    transition: 'color 0.1s',
                  }}
                >
                  {col}
                  {sortCol === col && (
                    <span style={{ marginLeft: 4, fontSize: 10, color: 'var(--accent)' }}>
                      {sortDir === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, i) => (
              <tr
                key={i}
                style={{ borderBottom: '1px solid var(--border)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                onMouseLeave={e => e.currentTarget.style.background = ''}
              >
                {columns.map(col => {
                  const v = row[col];
                  const isNum = typeof v === 'number';
                  const isNull = v == null;
                  return (
                    <td key={col} style={{
                      padding: '8px 16px',
                      color: isNull ? 'var(--text3)' : 'var(--text)',
                      fontStyle: isNull ? 'italic' : 'normal',
                      whiteSpace: 'nowrap', maxWidth: 320,
                      overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {isNull ? 'null' : isNum
                        ? (Number.isInteger(v) ? v.toLocaleString() : v.toFixed(4))
                        : String(v)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination — Macarowni style */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10,
          padding: '6px 16px', borderTop: '1px solid var(--border)',
          background: 'var(--bg2)', flexShrink: 0, fontSize: 12, color: 'var(--text2)',
        }}>
          <span>
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, rows.length)} of {rows.length.toLocaleString()}
          </span>
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            style={{
              background: 'none', border: 'none', cursor: page === 0 ? 'not-allowed' : 'pointer',
              color: page === 0 ? 'var(--text3)' : 'var(--text2)', display: 'flex', padding: 2,
            }}
          ><ChevronLeft size={14} /></button>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page === totalPages - 1}
            style={{
              background: 'none', border: 'none', cursor: page === totalPages - 1 ? 'not-allowed' : 'pointer',
              color: page === totalPages - 1 ? 'var(--text3)' : 'var(--text2)', display: 'flex', padding: 2,
            }}
          ><ChevronRight size={14} /></button>
        </div>
      )}
    </div>
  );
}
